import { Commodity } from 'services/delivery/types';
import { AnnualUsageUnit, CustomerType } from 'services/orders/types';

export enum PriceUnit {
  US_CENT = 'US_CENT',
  USD = 'USD',
}

export enum TermUnit {
  MONTH = 'MONTH',
}

export enum BillingMethod {
  UTILITY_CONSOLIDATED = 'UCB',
}

export interface Supplier {
  supplierId: string;
  name: string;
  logoUrl: string;
}

export interface Price {
  amount: number;
  currencyCode: keyof typeof PriceUnit;
  amountMinor?: number;
}

export interface Term {
  value: number;
  unit: keyof typeof TermUnit;
}

export interface EstimatedSavings {
  amount: number;
  currencyCode: keyof typeof PriceUnit;
}

type AttachDocument = { url: string; format: string } | string;

export interface Plan {
  planId: string;
  supplier: Supplier;
  price: Price;
  term: Term;
  estimatedSavings: EstimatedSavings;
  billingMethod: keyof typeof BillingMethod;
  commodity?: Commodity;
  loadFactor?: number;
  bandwidth: number;
  greenEnergy: number;
  annualUsage: {
    maximum: number;
    minimum: number;
    unit: AnnualUsageUnit;
  };
  effectiveDate?: string;
  cancellationFee?: string;
  documents?: Record<string, AttachDocument>;
  isTheBest?: boolean;
  energySource?: 'WIND' | 'SOLAR';
}

export interface GetBestPlansRequest {
  annualUsage: number;
  unit: AnnualUsageUnit;
  commodity: Commodity;
  state: string;
  customerType: CustomerType;
  zipCode?: string;
  utilityId?: string;
  effectiveDate?: string;
  zoneId?: string;
  classificationId?: string;
  priceToCompare?: number;
  preferredTerms?: string;
  brokerId?: string;
}

export interface PlansQueryFilter extends GetBestPlansRequest {
  term: string;
}

export interface GetPlansRequest extends PlansQueryFilter {
  sort: string;
  page: number;
  size: number;
}

export interface GetBestPlansApiRequest {
  'zone-id'?: string;
  'utility-id': string;
  'classification-id'?: string;
  'annual-usage': string;
  'compare-to-price'?: number;
  'effective-date': string;
  'customer-type': CustomerType;
  'zip-code': string;
  'broker-id': string;
  commodity: string;
  state?: string;
}

export interface GetPlansApiRequest extends GetBestPlansApiRequest {
  'term.range': string;
  sort: string;
  page: number;
  size: number;
}

export interface GetPlansResponse {
  plans: Plan[];
  total: number;
}

export interface GetBestPlansResponse {
  bestPlans: Plan[];
}

export interface GetBestPriceRequest {
  customerType: CustomerType;
  commodity: Commodity;
  brokerId?: string;
  zipCode?: string;
  state?: string;
  effectiveDate?: string;
}

export interface GetBestPriceApiRequest {
  'customer-type': CustomerType;
  commodity: string;
  'broker-id': string;
  'zip-code'?: string;
  state?: string;
  'effective-date'?: string;
}

export interface QuotesServiceInterface {
  getPlans(request: GetPlansRequest): Promise<GetPlansResponse>;
  getBestPlans(request: GetBestPlansRequest): Promise<GetBestPlansResponse>;
  getBestPrice(request: GetBestPriceRequest): Promise<Plan>;
  getPlan(planId: string): Promise<Plan>;
}
